import React, { useCallback, useEffect, useReducer } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import ReactModal from "react-modal";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import NumberFormat from "react-number-format";
import moment from "moment-timezone";
import { formatTripDateRange } from "../../helpers/dates";
import { useDebouncedCallback } from "use-debounce";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";

import ai from "../../helpers/axios";
import { LoadingButton } from "../shared/loading-button";
import TextAreaInput from "../textarea-input";
import currencyFormatter from "../../helpers/currencyFormatter";
import { formatProspectOption } from "./helpers";
import getApiErrorMessage from "../../helpers/get-api-error-message";

const DEFAULT_OPTIONS_MAX_LIMIT = 50;

const ACTION_OPTIONS = [
  { label: "Charge Card", value: "chargeCard" },
  { label: "Comp", value: "comp" },
];

const formatTripOptionWithClient = ({ _id, tripTag, firstDepartureDate, lastArrivalDate, routing, clientName }) => ({
  value: _id,
  label: [
    tripTag,
    formatTripDateRange(firstDepartureDate, lastArrivalDate),
    clientName,
    routing ?? "-",
  ].filter(Boolean).join(" — "),
});

const formatLinkedTripLabel = (row) => [
  row?.trip?.tripTag,
  formatTripDateRange(row?.trip?.firstDepartureDate, row?.trip?.lastArrivalDate),
  row?.clientOrOperator,
  row?.trip?.routing ?? "-",
].filter(Boolean).join(" — ");

const getTotal = (row) => Math.abs(row?.amount || 0);

const buildInitialState = (row) => ({
  isSaving: false,
  prospectOption: null,
  tripOption: null,
  actionOption: null,
  amountToCharge: null,
  amountToComp: null,
  notes: "",
});

const reducer = (state, action) => {
  switch (action.type) {
    case "fieldChanged":
      return { ...state, [action.field]: action.value };
    case "actionChanged":
      return {
        ...state,
        actionOption: action.value,
        amountToCharge: action.value?.value === "comp" ? state.amountToCharge : null,
        amountToComp: action.value?.value === "comp" ? state.amountToComp : null,
      };
    case "amountToChargeChanged": {
      const amountToCharge = Math.min(Math.max(action.value ?? 0, 0), action.total);
      return { ...state, amountToCharge, amountToComp: action.total - amountToCharge };
    }
    case "amountToCompChanged": {
      const amountToComp = Math.min(Math.max(action.value ?? 0, 0), action.total);
      return { ...state, amountToComp, amountToCharge: action.total - amountToComp };
    }
    case "reset":
      return buildInitialState(action.row);
    default:
      return state;
  }
};

const getProspects = async (token, params = {}) => {
  const { data } = await ai.auth(token).get("/api/prospects/search", { params });
  return data?.prospects ?? [];
};

const getProspectOptions = (prospects) =>
  prospects.flatMap(({ _id, firstAndLastName, lastName, firstName, emails, companyName, assigneeId, assigneeName }) => {
    if (!firstAndLastName && !lastName && !firstName) return [];
    return [formatProspectOption({
      _id,
      firstAndLastName,
      lastName,
      firstName,
      emails,
      companyName,
      assigneeId,
      assigneeName,
    })];
  });

const getTrips = async (token, params = {}) => {
  const { data } = await ai.auth(token).get("/api/trips/search", { params });
  return data?.data ?? [];
};

const CompChargeModal = ({ isOpen, mode, row, onRequestClose }) => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const [state, dispatch] = useReducer(reducer, buildInitialState(row));

  useEffect(() => {
    if (isOpen) {
      dispatch({ type: "reset", row });
    }
  }, [isOpen, row?._id, row?._lineItemID]);

  const getToken = useCallback(async () => {
    return getAccessTokenSilently().catch(() => loginWithRedirect());
  }, [getAccessTokenSilently, loginWithRedirect]);

  const handleClose = useCallback(() => {
    dispatch({ type: "reset", row });
    onRequestClose(false);
  }, [onRequestClose, row]);

  const loadProspects = useCallback(async (inputValue) => {
    const token = await getToken();
    const prospects = await getProspects(token, { q: inputValue, limit: DEFAULT_OPTIONS_MAX_LIMIT });
    return getProspectOptions(prospects);
  }, [getToken]);

  const debouncedLoadProspects = useDebouncedCallback(
    (inputValue, callback) => {
      loadProspects(inputValue).then(callback).catch(() => callback([]));
    },
    500,
  );

  const loadTrips = useCallback(async (inputValue) => {
    const token = await getToken();
    const trips = await getTrips(token, {
      q: inputValue,
      limit: DEFAULT_OPTIONS_MAX_LIMIT,
      prospectFilter: state.prospectOption?.value,
      restrictToOwnTrips: true,
    });
    return trips.map(formatTripOptionWithClient);
  }, [getToken, state.prospectOption?.value]);

  const debouncedLoadTrips = useDebouncedCallback(
    (inputValue, callback) => {
      loadTrips(inputValue).then(callback).catch(() => callback([]));
    },
    500,
  );

  const total = getTotal(row);

  const handleSubmit = useCallback(async () => {
    if (mode === "assign" && !state.tripOption?.value) {
      return toast.error("Please select a trip.");
    }

    if (!state.actionOption?.value) {
      return toast.error("Please select an action.");
    }

    if (
      state.actionOption.value === "comp" &&
      Math.round(((state.amountToCharge ?? 0) + (state.amountToComp ?? 0)) * 100) !== Math.round(total * 100)
    ) {
      return toast.error("Amount to Charge and Amount to Comp must add up to the Total.");
    }

    dispatch({ type: "fieldChanged", field: "isSaving", value: true });

    const token = await getToken();

    const payload = {
      lineItemID: row?._lineItemID || null,
      actionType: state.actionOption.value,
      amountToCharge: state.actionOption.value === "comp" ? state.amountToCharge : total,
      amountToComp: state.actionOption.value === "comp" ? state.amountToComp : 0,
      notes: state.notes || "",
      ...(mode === "assign" ? {
        tripId: state.tripOption.value,
        prospectId: state.prospectOption?.value || null,
      } : {}),
    };

    const endpoint = mode === "assign"
      ? `/api/xero/transactions/${row._id}/assign-charge`
      : `/api/xero/transactions/${row._id}/comp-charge`;

    const response = await ai.auth(token)
      .post(endpoint, payload)
      .catch((error) => {
        Sentry.captureException(error);
        toast.error(getApiErrorMessage(error, "Error saving the charge."));
      });

    if (response?.data) {
      toast.success("Charge saved successfully.");
      dispatch({ type: "reset", row });
      await onRequestClose();
    }

    dispatch({ type: "fieldChanged", field: "isSaving", value: false });
  }, [mode, row, state, total, getToken, onRequestClose]);

  return (
    <ReactModal
      isOpen={isOpen}
      shouldCloseOnEsc={false}
      shouldCloseOnOverlayClick={false}
      onRequestClose={handleClose}
      style={{
        overlay: { zIndex: 999999, backgroundColor: "rgb(32 53 68 / 50%)" },
        content: {
          width: "auto",
          border: "none",
          maxWidth: "700px",
          maxHeight: "85vh",
          minWidth: "calc(min(600px, 95vw))",
          borderRadius: "0",
          boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
        },
      }}
      appElement={document.getElementById("app")}
    >
      <div className="modal-header">
        <h4>{mode === "assign" ? "Assign Charge to a Trip" : "Comp Charge"}</h4>
      </div>

      <div className="modal-body width-100 modal-body-with-scroll">
        <div className="w-100 d-flex gap-20 mb-15">
          <div className="flex-half">
            <div><b>Date:</b> {row?.transactionDate ? moment(row.transactionDate).format("M/D/YY") : "-"}</div>
            <div><b>Card:</b> {row?.cardName || "-"}</div>
            <div><b>Contact Name:</b> {row?.contactName || "-"}</div>
          </div>
          <div className="flex-half">
            <div><b>Category:</b> {row?.categoryLabel || "-"}</div>
            <div><b>Reference:</b> {row?.reference || "-"}</div>
            <div><b>Amount:</b> {currencyFormatter.format(row?.amount || 0)}</div>
          </div>
        </div>

        <form className="w-100 base-form mt-10">
          {mode === "assign" ? (
            <>
              <div className="form-group">
                <label className="base-label">Filter by Prospect</label>
                <AsyncSelect
                  classNamePrefix="select"
                  loadOptions={debouncedLoadProspects}
                  placeholder="Search/Select Prospect"
                  onChange={(option) => {
                    dispatch({ type: "fieldChanged", field: "prospectOption", value: option });
                    dispatch({ type: "fieldChanged", field: "tripOption", value: null });
                  }}
                  value={state.prospectOption}
                  isClearable
                  menuPosition="fixed"
                />
              </div>

              <div className="form-group">
                <label className="base-label">
                  Filter by Trip <span className="required">*</span>
                </label>
                <AsyncSelect
                  classNamePrefix="select"
                  loadOptions={debouncedLoadTrips}
                  placeholder="Search/Select Trip"
                  onChange={(option) => {
                    dispatch({ type: "fieldChanged", field: "tripOption", value: option });
                  }}
                  value={state.tripOption}
                  isClearable
                  menuPosition="fixed"
                />
              </div>
            </>
          ) : (
            <div className="form-group">
              <label className="base-label">Trip</label>
              <input className="base-input" disabled value={formatLinkedTripLabel(row)} />
            </div>
          )}

          <div className="form-group">
            <label className="base-label">
              Action <span className="required">*</span>
            </label>
            <Select
              classNamePrefix="select"
              options={ACTION_OPTIONS}
              placeholder="Select Action"
              value={state.actionOption}
              onChange={(option) => dispatch({ type: "actionChanged", value: option })}
              menuPosition="fixed"
            />
          </div>

          {state.actionOption?.value === "comp" && (
            <div className="form-group">
              <label className="base-label">Enter Comp Amount</label>

              <div className="w-100 d-flex gap-20">
                <div className="form-group flex-half">
                  <label className="base-label">
                    Amount to Charge <span className="required">*</span>
                  </label>
                  <NumberFormat
                    className="base-input"
                    thousandSeparator
                    prefix="$"
                    decimalScale={2}
                    fixedDecimalScale
                    allowNegative={false}
                    placeholder="$0.00"
                    value={state.amountToCharge ?? ""}
                    onValueChange={({ floatValue }) => {
                      dispatch({ type: "amountToChargeChanged", value: floatValue, total });
                    }}
                  />
                </div>

                <div className="form-group flex-half">
                  <label className="base-label">
                    Amount to Comp <span className="required">*</span>
                  </label>
                  <NumberFormat
                    className="base-input"
                    thousandSeparator
                    prefix="$"
                    decimalScale={2}
                    fixedDecimalScale
                    allowNegative={false}
                    placeholder="$0.00"
                    value={state.amountToComp ?? ""}
                    onValueChange={({ floatValue }) => {
                      dispatch({ type: "amountToCompChanged", value: floatValue, total });
                    }}
                  />
                </div>

                <div className="form-group flex-half">
                  <label className="base-label">Total</label>
                  <NumberFormat
                    className="base-input"
                    thousandSeparator
                    prefix="$"
                    decimalScale={2}
                    fixedDecimalScale
                    disabled
                    value={total}
                  />
                </div>
              </div>
            </div>
          )}

          <TextAreaInput
            label="Notes"
            name="notes"
            containerClasses="mb-0"
            value={state.notes}
            handleChange={(e) => {
              dispatch({ type: "fieldChanged", field: "notes", value: e.target.value });
            }}
          />
        </form>
      </div>

      <div className="modal-footer no-border w-100 d-flex gap-10">
        <LoadingButton
          className="base-button evo-blue flex-half"
          onClick={handleSubmit}
          isLoading={state.isSaving}
          disabled={(mode === "assign" && !state.tripOption?.value) || !state.actionOption?.value}
        >
          Submit
        </LoadingButton>

        <button
          className="base-button btn-danger flex-half"
          onClick={handleClose}
          disabled={state.isSaving}
        >
          Cancel
        </button>
      </div>
    </ReactModal>
  );
};

export default CompChargeModal;
